import { useMsal } from '@azure/msal-react';
import { Button, Checkbox, mergeClasses, SearchBox, Text } from '@fluentui/react-components';
import { ArrowClockwiseFilled } from '@fluentui/react-icons';
import { parseISO } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { round } from 'es-toolkit';
import parse from 'html-react-parser';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { useCommonStyles, useStores, useTitle, useWindowSize } from '../../common/hooks';
import { getCleanHtmlString } from '../../common/html-string/get-clean-html-string';
import { AcceptItem, AwardGroupRef } from '../../models';
import { NominatePageRouteStates } from '../../models/route-states';
import { RoutePaths } from '../../route-paths';
import { CustomMessageBar } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { LoadingSpinner } from '../shared-components/LoadingSpinner';
import { AcceptItemModal } from './components/AcceptItemModal';
import { AccordionView } from './components/AccordionView';
import { ContentImage } from './components/ContentImage';
import { EligibilitySupportModal } from './components/EligibilitySupportModal';
import './SelectAwardPage.scss';

interface SelectAwardPageProps {
  loggedInUserId: number;
}

const SelectAwardPageImpl: React.FC<SelectAwardPageProps> = (props) => {
  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    userStore,
    selectAwardPageUiStore,
  } = useStores();

  const { width } = useWindowSize();
  const commonStyles = useCommonStyles();

  const { loggedInUserId } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [isShowEligibilitySupportModal, setShowEligibilitySupportModal] = useState<boolean>(false);

  const getCurrentUserPartnerIds = async (): Promise<void> => {
    if (!userStore.userInfo) return;
    try {
      const mpnIds = await apiStore.getCurrentUserPartnerIds(instance, userStore.userInfo.userId);
      userStore.setUserMpnIds(mpnIds);
    } catch (error) {
      // TODO - handle error
      userStore.setUserMpnIds([]);
    }
  };

  const getAcceptItems = async (): Promise<void> => {
    try {
      var acceptItems = await apiStore.getCurrentUserAcceptItems(instance, loggedInUserId);
      selectAwardPageUiStore.setAcceptItems(acceptItems);
    } catch (error) {
      // TODO - handle error
    }
  }

  const onItemAccepted = async (acceptItem: AcceptItem): Promise<void> => {
    try {
      await apiStore.submitAcceptedItem(instance, loggedInUserId, acceptItem);
      selectAwardPageUiStore.setAcceptedItemIds(acceptItem.acceptItemId);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const getAwardGroups = async (): Promise<void> => {
    try {
      const awardGroups = await apiStore.getAwardGroups(instance);
      selectAwardPageUiStore.setAwardGroups(awardGroups);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const getEligibleAwardRefs = async (): Promise<void> => {
    try {
      const awardRefs = await apiStore.getEligibleAwardRefs(instance);
      selectAwardPageUiStore.setEligibleAwardRefs(awardRefs);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const getEnrolmentStatus = async (): Promise<void> => {
    try {
      const freshness = await apiStore.getEligibilityStatus(instance);
      selectAwardPageUiStore.setEnrolmentFreshness(freshness);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const syncEligibleAwards = async (): Promise<void> => {
    await Promise.allSettled([
      getEligibleAwardRefs(),
      getAwardGroups(),
      getEnrolmentStatus(),
    ]);
  };

  const syncEnrolments = async (): Promise<void> => {
    selectAwardPageUiStore.setEnrolmentSyncProgress(0);
    await apiStore.syncEligibility(
      instance,
      (progress) => selectAwardPageUiStore.setEnrolmentSyncProgress(progress),
    );
  }

  const handleSyncEnrolments = async (): Promise<void> => {
    selectAwardPageUiStore.setIsCheckingEligibility(true);
    try {
      await syncEnrolments();
      await syncEligibleAwards();
    } catch (error) {
      // TODO: Handle error
    } finally {
      selectAwardPageUiStore.setIsCheckingEligibility(false);
    }
  }

  const onAwardSelected = async (awardGroupRef: AwardGroupRef): Promise<void> => {
    setIsLoading(true);
    try {
      const award = await apiStore.getAwardById(instance, awardGroupRef.awardId);
      selectAwardPageUiStore.setSelectedAward(award);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
      selectAwardPageUiStore.resetAcceptance();
    }
  }

  const debouncedSearch = useDebouncedCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        const awardGroups = await apiStore.getAwardGroups(instance, { searchText: query });
        selectAwardPageUiStore.setAwardGroups(awardGroups);
      }
      finally {
        setIsLoading(false);
      }
    },
    1000
  );

  const onSearchTextChanged = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  }

  const sendDiscrepancyReport = async (): Promise<void> => {
    try {
      const discrepancyReportRef = await apiStore.createDiscrepancyReport(instance, {
        discrepancyReportId: 0,
        awardId: selectAwardPageUiStore.selectedAward?.awardId ?? 0,
        emailAddress: userStore.userInfo?.email ?? '',
        MPNID: userStore.userMpnIds.map(mpnId => mpnId.mpnId).join(',') ?? '',
      });
      if (discrepancyReportRef.discrepancyReportId <= 0) return;
      selectAwardPageUiStore.setIsDiscrepancyReportTicketSent(true);
      setShowEligibilitySupportModal(false);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const loadAwards = async (): Promise<void> => {
    if (selectAwardPageUiStore.needToAcceptItems) {
      selectAwardPageUiStore.setIsCheckingEligibility(false);
      return;
    }

    selectAwardPageUiStore.setIsCheckingEligibility(true);

    try {
      await syncEligibleAwards();

      if (selectAwardPageUiStore.enrolmentFreshness?.hasUnsyncedAccounts) {
        await syncEnrolments();
        await syncEligibleAwards();
      }
    } finally {
      selectAwardPageUiStore.setIsCheckingEligibility(false);
    }
  };

  const preloadData = async () => {
    setIsLoading(true);

    await Promise.allSettled([
      getAcceptItems(),
      getCurrentUserPartnerIds(),
    ]);

    setIsLoading(false);
  };

  useTitle('Award Selection');

  useEffect(() => {
    preloadData();
    return () => {
      selectAwardPageUiStore.resetStates();
    }
  }, []);

  useEffect(() => {
    loadAwards();
  }, [
    selectAwardPageUiStore.needToAcceptItems
  ]);

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const checkingEligibilityLabel = selectAwardPageUiStore.enrolmentSyncProgress > 0
    ? `Checking eligibility (${round(selectAwardPageUiStore.enrolmentSyncProgress)}%)...`
    : 'Checking eligibility...';

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const lastSyncedLocalDate = selectAwardPageUiStore.enrolmentFreshness?.lastUpdatedDateTime
    ? utcToZonedTime(parseISO(selectAwardPageUiStore.enrolmentFreshness.lastUpdatedDateTime), userTimeZone)
    : undefined;
  const lastSyncedLabel = lastSyncedLocalDate
    ? `Designations & Specializations as of ${format(lastSyncedLocalDate, 'PPp zzz', { timeZone: userTimeZone })}`
    : undefined;

  return (
    <main
      className='select-award-page'
    >
      {
        selectAwardPageUiStore.needToAcceptItems &&
        <AcceptItemModal
          onAgreeButtonClick={(acceptItem) => onItemAccepted(acceptItem)}
        />
      }
      {
        isLoading && (
          <Loading
            isFullScreen
          />
        )
      }
      {
        isShowEligibilitySupportModal &&
        <EligibilitySupportModal
          open={isShowEligibilitySupportModal}
          onCloseButtonClicked={() => setShowEligibilitySupportModal(false)}
          onConfirmButtonClicked={() => sendDiscrepancyReport()}
        />
      }
      <div
        className='content'
      >
        <div
          className='award-groups'
        >
          <div
            className='award-groups__title'
          >
            <Text
              size={500}
              weight='semibold'
              as='h1'
            >
              Select an award
            </Text>
          </div>
          {
            selectAwardPageUiStore.isCheckingEligibility && (
              <FlexBox
                direction='column'
                content='center'
                aligns='center'
                isFullWidth
                className='award-groups__checking-eligibility'
              >
                <LoadingSpinner
                  size='large'
                  label={checkingEligibilityLabel}
                  labelPosition='below'
                />
              </FlexBox>
            )
          }
          {
            !selectAwardPageUiStore.isCheckingEligibility && (
              <>
                <div
                  className='award-groups__search'
                >
                  <SearchBox
                    placeholder='Search'
                    value={searchText}
                    onChange={(_, data) => onSearchTextChanged(data.value)}
                  />
                </div>
                <div role='region' aria-live='polite' aria-atomic>
                  {selectAwardPageUiStore.awardGroups.length > 0 && <span className='sr-only'>{`${selectAwardPageUiStore.awardGroups.length} award groups was found`}</span>}
                </div>
                <div role='region' aria-live='assertive' aria-atomic>
                  {selectAwardPageUiStore.awardGroups.length === 0 && <span className='sr-only'>No award groups was found</span>}
                </div>
                <AccordionView
                  onAwardSelected={(award) => onAwardSelected(award)}
                />
                <FlexBox
                  direction='column'
                  className='award-groups__eligibility-refresh'
                >
                  <Button
                    icon={<ArrowClockwiseFilled color='#0F6CBD' />}
                    appearance='secondary'
                    onClick={() => handleSyncEnrolments()}
                  >
                    Refresh Designations & Specializations
                  </Button>
                  {
                    lastSyncedLabel && (
                      <Text
                        align='center'
                        as='p'
                      >
                        {lastSyncedLabel}
                      </Text>
                    )
                  }
                </FlexBox>
              </>
            )
          }
        </div>
        <div
          className='award-detail'
        >
          <div
            className='award-detail-title-placeholder'
          >
          </div>
          {
            selectAwardPageUiStore.selectedAward && (
              <div
                className='award-detail-content'
              >
                <h2
                  className='award-title'
                >
                  {selectAwardPageUiStore.selectedAward.title}
                </h2>
                {
                  width < 600 && (
                    <CustomMessageBar
                      message='The nomination process is not supported through your mobile device. Please use your desktop or tablet to submit your Partner of the Year nominations.'
                      notificationTypeCode='warning'
                      showDismissButton
                    />
                  )
                }
                {
                  selectAwardPageUiStore.isSelectedAwardNotEligible &&
                  <CustomMessageBar
                    notificationTypeCode='warning'
                    showDismissButton
                    message={
                      <>
                        <Text
                          as='p'
                        >
                          Currently, your team is ineligible to apply to the below award. Please review eligibility requirements below. If your company is qualified but designations and/or co-sell status are not applying correctly, please contact our support team as soon as possible to verify to ensure this is resolved <Text weight='semibold'>before the nomination window closes on July 7th at 6:00pm PT</Text>.{' '}
                        </Text>
                        <button
                          className='button-as-link'
                          onClick={() => setShowEligibilitySupportModal(true)}
                        >
                          Click here for help
                        </button>
                      </>
                    }
                  />
                }
                <div className={mergeClasses(commonStyles.richTextContent, 'award-description')}>
                  {parse(getCleanHtmlString(selectAwardPageUiStore.selectedAward.summary))}
                </div>
                <FlexBox
                  direction='column'
                  className='user-acceptance-checkboxes'
                >
                  <Checkbox
                    label='I have read and reviewed that my company meets all the requirements for this award.'
                    onChange={(_, data) =>
                      selectAwardPageUiStore.setIsRequirementsAccepted(data.checked === true)
                    }
                    checked={selectAwardPageUiStore.isRequirementsAccepted}
                    disabled={selectAwardPageUiStore.isSelectedAwardNotEligible}
                  />
                  <Checkbox
                    label='I confirm that I have read and agree to the Contest Rules.'
                    onChange={(_, data) =>
                      selectAwardPageUiStore.setIsContestRulesAccepted(data.checked === true)
                    }
                    checked={selectAwardPageUiStore.isContestRulesAccepted}
                    disabled={selectAwardPageUiStore.isSelectedAwardNotEligible}
                  />
                </FlexBox>
                <div className='navigation'>
                  <Link
                    to={RoutePaths.Home}
                    className='secondary-btn'
                  >
                    Back
                  </Link>
                  {
                    selectAwardPageUiStore.isAccepted ?
                      <Link
                        to={RoutePaths.Nominate}
                        state={{ selectedAwardId: selectAwardPageUiStore.selectedAward.awardId } satisfies NominatePageRouteStates}
                        className='primary-btn'
                      >
                        Continue
                      </Link> :
                      <span>Continue</span>
                  }
                </div>
              </div>
            )
          }
          {
            !selectAwardPageUiStore.selectedAward && (
              <div
                className='award-begin-nomination'
              >
                <div
                  className='award-begin-nomination-background'
                >
                  <div className='potya-infographic-image'>
                    <ContentImage />
                  </div>
                  <div className='award-begin-nomination-description'>
                    <div className='text-description'>
                      <p><b>Access the complete list of guidelines, rules and regulations, FAQs and more in the <a href={`${window.location.origin}/award-resources`}>Award Resources tab</a>.</b></p>
                    </div>
                  </div>
                </div>
              </div>
            )
          }
        </div>
      </div>
    </main>
  );
}

export const SelectAwardPage = observer(SelectAwardPageImpl);
